import type { Node } from "@xyflow/react";
import { useCanvasStore } from "@/stores/canvas-store";
import type { FrameData } from "@/lib/frame-data";
import { setFrameHidden } from "@/lib/frame-visibility";

/** Clear both focus-hidden and durable hidden state for the given frames. */
export function unhideCanvasFrames(frameIds: string[]): void {
  const state = useCanvasStore.getState();
  const ids = new Set(frameIds);
  if (frameIds.some((id) => state.focusHiddenFrameIds.has(id))) {
    state.setFocusHiddenFrames(
      [...state.focusHiddenFrameIds].filter((id) => !ids.has(id)),
    );
  }
  for (const node of state.nodes) {
    if (ids.has(node.id) && node.data.metadata?.hidden) {
      setFrameHidden(node.id, false);
    }
  }
  // A node is hidden only through those two sources, but a node can still
  // carry a `hidden` that neither of them holds any more. The two steps above
  // skip such a node, and a caller waiting for these frames to show would
  // wait forever, so the requested frames take the rule the store applies.
  useCanvasStore.setState((current) => {
    const stale = (node: Node<FrameData>) =>
      ids.has(node.id) &&
      !!node.hidden &&
      !node.data.metadata?.hidden &&
      !current.focusHiddenFrameIds.has(node.id);
    if (!current.nodes.some(stale)) return current;
    return {
      nodes: current.nodes.map((node) =>
        stale(node) ? { ...node, hidden: false } : node,
      ),
    };
  });
}
